/* eslint-disable @typescript-eslint/no-explicit-any */

import config from '@/config';
import { executeRestForm } from '@/rest/executeRest';

interface FileResourceResponse {
  url: string;
}

export async function uploadFileResource(
  data: File,
  customerId: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', data);
  formData.append('customerId', customerId);

  const response = await executeRestForm(config.rest.url + '/resource', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${data.name}`);
  }

  const result = (await response.json()) as FileResourceResponse;
  return result.url; // Assuming the API returns a URL
}

/**
 * Recursively uploads file resources in an object or array.
 * If the data is an array, it processes each item.
 * If the data is an object, it uploads each file property and replaces it with the URL.
 */
export async function uploadFileResourcesInObject(
  src: any,
  customerId: string,
): Promise<any> {
  // If data is an array, process each item
  if (Array.isArray(src)) {
    return Promise.all(
      src.map(async (_, idx) => {
        if (src[idx] instanceof File) {
          src[idx] = await uploadFileResource(src[idx], customerId);
        } else {
          await uploadFileResourcesInObject(src[idx], customerId);
        }
      }),
    );
  } else if (src !== null && typeof src === 'object') {
    // If data is an object, upload each file property and replace it with the URL
    const srcObject = src as Record<string, any>;
    const keys = Object.keys(srcObject);
    return Promise.all(
      keys.map(async (key) => {
        if (srcObject[key] instanceof File) {
          srcObject[key] = await uploadFileResource(srcObject[key], customerId);
        } else {
          await uploadFileResourcesInObject(srcObject[key], customerId);
        }
      }),
    );
  } else {
    return undefined;
  }
}
